/**
 * Streamdown 2.6 word tokens and fade timing, without running the text
 * through Markdown. Streamdown does not export its splitter, and reasoning
 * must stay plain pre-text so unfenced diffs do not become lists.
 */
export const REASONING_STREAM_FADE = {
  animation: 'sd-fadeIn',
  durationMs: 150,
  easing: 'ease-out',
  staggerMs: 15,
  maxBacklogMs: 200,
} as const;

const WHITESPACE = /\s/;
const ONLY_WHITESPACE = /^\s+$/;

export type ReasoningStreamNode =
  | { kind: 'text'; start: number; value: string }
  | { kind: 'word'; start: number; value: string; delayMs: number; durationMs: number };

export function splitStreamdownWords(text: string): string[] {
  const tokens: string[] = [];
  let current = '';
  let inWhitespace = false;

  for (const character of text) {
    const isWhitespace = WHITESPACE.test(character);
    if (isWhitespace !== inWhitespace && current.length > 0) {
      if (isWhitespace) {
        current += character;
        inWhitespace = true;
        continue;
      }
      tokens.push(current);
      current = '';
    }
    current += character;
    inWhitespace = isWhitespace;
  }

  if (current.length > 0) {
    tokens.push(current);
  }

  return tokens;
}

function staggerStep(newWordCount: number): number {
  if (newWordCount <= 1) {
    return REASONING_STREAM_FADE.staggerMs;
  }

  const fullSpan = (newWordCount - 1) * REASONING_STREAM_FADE.staggerMs;
  if (fullSpan <= REASONING_STREAM_FADE.maxBacklogMs) {
    return REASONING_STREAM_FADE.staggerMs;
  }

  return REASONING_STREAM_FADE.maxBacklogMs / (newWordCount - 1);
}

export function planReasoningStreamNodes(
  text: string,
  committedLength: number,
): ReasoningStreamNode[] {
  const committedEnd = Math.min(Math.max(committedLength, 0), text.length);
  const committed = text.slice(0, committedEnd);
  const incoming = text.slice(committedEnd);
  const nodes: ReasoningStreamNode[] = [];

  if (committed.length > 0) {
    nodes.push({ kind: 'text', start: 0, value: committed });
  }

  const tokens = splitStreamdownWords(incoming);
  const newWordCount = tokens.reduce(
    (count, value) => (ONLY_WHITESPACE.test(value) ? count : count + 1),
    0,
  );
  const step = staggerStep(newWordCount);
  let offset = committedEnd;
  let newIndex = 0;

  for (const value of tokens) {
    const start = offset;
    offset += value.length;
    if (ONLY_WHITESPACE.test(value)) {
      nodes.push({ kind: 'text', start, value });
      continue;
    }

    nodes.push({
      kind: 'word',
      start,
      value,
      delayMs: newIndex * step,
      durationMs: REASONING_STREAM_FADE.durationMs,
    });
    newIndex += 1;
  }

  return nodes;
}
